import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";
import { DIAL_CODES, flagEmoji, detectCountry, getDialEntry } from "@/lib/dialCodes";

/**
 * Split phone-number input — country picker (flag + dial code) on the left,
 * national number on the right. The combined `+{dial}{number}` is written
 * back via `onChange(combined)` so existing validators / payloads continue
 * to work without changes.
 *
 * Behaviour:
 *  • The country dropdown is auto-selected from the user's browser/profile
 *    country on first mount (or from a parsed value if the existing string
 *    already starts with a known +CC).
 *  • The number field strips any leading "0" automatically (per IATA national
 *    convention when prefixed with an international dial code).
 *  • Typing in the number always allows digits, spaces, hyphens and a leading
 *    "+" — but the leading "0" is stripped silently to match the carrier's
 *    expected format.
 */
export default function PhoneInput({
  value,
  onChange,
  error,
  testId = "phone-input",
  defaultCountry,
  className = "",
}) {
  // ── Initial state — try to parse an existing +CC out of `value` ─────────
  const parsed = useMemo(() => parsePhone(value), [value]);
  const [country, setCountry] = useState(
    () => parsed.country || defaultCountry || detectCountry("AE")
  );
  const [national, setNational] = useState(parsed.national || "");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  // Keep parent's combined value in sync. Skip if both pieces empty so we
  // don't write a lone "+971" into a fresh form before the user types.
  useEffect(() => {
    const dial = getDialEntry(country)?.[1] || "";
    const combined = national ? `+${dial}${national}` : "";
    if (combined !== value) onChange(combined);
  }, [country, national, value, onChange]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DIAL_CODES;
    return DIAL_CODES.filter(([c, dial, name]) =>
      name.toLowerCase().includes(q) || dial.startsWith(q.replace(/^\+/, "")) || c.toLowerCase().startsWith(q)
    );
  }, [query]);

  const onNumberChange = (e) => {
    let v = e.target.value.replace(/[^\d\s-]/g, "");
    // Strip any leading "0" — common national convention when an international
    // dial code is selected. e.g. UAE "0546667" → "546667" with +971.
    v = v.replace(/^0+/, "");
    setNational(v);
  };

  const dial = getDialEntry(country)?.[1] || "";
  const flag = flagEmoji(country);

  return (
    <div className={`relative ${className}`} ref={wrapRef}>
      <div className={`flex items-stretch rounded-md border ${error ? "border-red-400" : "border-input"} bg-background h-11 overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0`}>
        {/* Country / dial-code trigger */}
        <button
          type="button"
          data-testid={`${testId}-cc`}
          onClick={() => { setOpen((o) => !o); setQuery(""); }}
          className="inline-flex items-center gap-1.5 px-3 bg-slate-50 hover:bg-slate-100 border-r border-slate-200 text-sm select-none"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="text-base leading-none" aria-hidden="true">{flag}</span>
          <span className="font-mono text-xs text-[var(--brand-accent)] font-semibold">+{dial}</span>
          <ChevronDown className="w-3 h-3 text-slate-500" />
        </button>
        {/* National number */}
        <Input
          data-testid={testId}
          type="tel"
          inputMode="numeric"
          value={national}
          onChange={onNumberChange}
          placeholder="546667890"
          className="flex-1 h-full border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none rounded-r-md"
        />
      </div>

      {/* Country dropdown */}
      {open && (
        <div
          data-testid={`${testId}-cc-dropdown`}
          className="absolute z-30 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-hidden flex flex-col"
        >
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or +dial code…"
              className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-200"
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.map(([c, d, name]) => (
              <button
                type="button"
                key={c + d}
                data-testid={`${testId}-cc-option-${c}`}
                onClick={() => { setCountry(c); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 flex items-center justify-between gap-2 ${country === c ? "bg-teal-50" : ""}`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-base leading-none">{flagEmoji(c)}</span>
                  <span className="text-[var(--brand-accent)]">{name}</span>
                </span>
                <span className="text-xs font-mono text-slate-500">+{d}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-slate-500">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Try to split a combined value back into {country, national}. Supports values
// that start with "+", and falls back to leaving everything in the national
// part if no dial-code can be matched (e.g. user pasted just digits).
function parsePhone(raw) {
  if (!raw || typeof raw !== "string") return { country: null, national: "" };
  const trimmed = raw.replace(/[\s-]/g, "");
  if (!trimmed.startsWith("+")) return { country: null, national: trimmed.replace(/^0+/, "") };
  const digits = trimmed.slice(1);
  // Try longest-prefix match first (some dials are 3 digits, like +971)
  for (const len of [4, 3, 2, 1]) {
    if (digits.length <= len) continue;
    const head = digits.slice(0, len);
    const entry = DIAL_CODES.find(([, d]) => d === head);
    if (entry) return { country: entry[0], national: digits.slice(len) };
  }
  return { country: null, national: digits };
}
